import { getOfferPriceId } from "./pricing"
import { isBasicPlanOffer, getVariantPagePath } from "./helpers"

function initApiDietPaymentProcessor(paymentBaseParams) {
  const API_URL = paymentBaseParams.apiUrl
  const PAYMODES = paymentBaseParams.paymodes

  // Start: Helpers
  const checkParams = (requiredParams, params) => {
    requiredParams.forEach((requiredParam) => {
      if (params[requiredParam] === undefined || params[requiredParam] === null) {
        throw new Error(`Missing required parameter: ${requiredParam}`)
      }
    })
  }

  const getPrimerMetadata = () => {
    if (pipelineConfig && pipelineConfig.primerMetadata) {
      return pipelineConfig.primerMetadata
    }

    return undefined
  }

  const getSuccessfulPaymentPath = (successPath) => {
    const basePath = `${window.location.origin}${pipelineConfig.pathPrefix}`
    const variantPath = getVariantPagePath({ pagePath: successPath, pipelineConfig })

    if (!variantPath) {
      throw new Error(`Success page path ${successPath} not found`)
    }

    return `${basePath}${variantPath}`
  }

  const getFailedPaymentPath = (failedPath) => {
    const basePath = `${window.location.origin}${pipelineConfig.pathPrefix}`
    const variantPath = getVariantPagePath({ pagePath: failedPath, pipelineConfig })

    if (!variantPath) {
      throw new Error(`Failed page path ${failedPath} not found`)
    }

    return `${basePath}${variantPath}`
  }

  const getPaymentMethodId = (paymentMethod) => {
    if (!PAYMODES[paymentMethod]) {
      throw new Error(`Payment method ${paymentMethod} not found`)
    }

    return PAYMODES[paymentMethod]
  }

  const getPipelineID = () => {
    const url = pipelineConfig.orderApiUrl
    const pipelinesMatchedString = url.match(/pipelines\/+\d+\//)

    return pipelinesMatchedString[0].split("/")[1] // Takes the number from the string - which is pipeline ID
  }

  const getGA4SessionId = () => {
    try {
      const cookies = document.cookie
        .split("; ")
        .find((item) => item.includes("_ga_"))
        .split("=")[1]
      return cookies.split(".")[2]
    } catch (e) {
      console.error(`GA4 session ID not found:`, e)
    }
    return undefined
  }

  const getCustomParameters = (successStepPath, failedStepPath, restrictCardSave) => {
    const store = Alpine.store("data")

    if (!store.values) {
      throw new Error("Store values not found")
    }

    const successfulUrl = getSuccessfulPaymentPath(successStepPath)
    const failedUrl = getFailedPaymentPath(failedStepPath)
    const activeOffer = store.values.activeOffer
    const isBasicPlan = isBasicPlanOffer(activeOffer)
    const isChance = isBasicPlan && store.values.exitIntent
    const isExternalSync = isBasicPlan ? true : false

    const params = {
      successful_payment_path: successfulUrl,
      failed_payment_path: failedUrl,
      chance: isChance,
      origin_id: store.values.id,
      origin_host: `https://${store.values.domain}`,
      origin_path: pipelineConfig.pathPrefix,
      utm_source: store.values.utm_source || "",
      utm_medium: store.values.utm_medium || "",
      utm_campaign: store.values.utm_campaign || "",
      utm_term: store.values.utm_term || "",
      utm_content: store.values.utm_content || "",
      initiated_remotely: restrictCardSave,
      external_sync: isExternalSync,
      token: store.values.token,
      ga_cid: store.values._ga?.match(/[^\.]+\.[^\.]+$/)?.[0],
      pipelineId: getPipelineID(),
      ga_session_id: getGA4SessionId(),
      fbc: store.values._fbc,
      fbp: store.values._fbp,
      fraud_net_session_id: store.values.fraud_net_session_id,
      fb_ads_id: store.values.fb_ads_id || undefined,
      fb_adset_id: store.values.fb_adset_id || undefined,
      segmentAnonymousId: store.values.ajs_anonymous_id || "",
      offerPriceId: getOfferPriceId({ offer: activeOffer, isChance }),
      primer_pipeline_metadata: {
        ...getPrimerMetadata(),
      },
    }

    return params
  }
  // End: Helpers

  // Start: Main functions
  /**
   * Creates a checkout session which consists of - URL, invoice and token, etc.
   * @param {string} successStepPath - Success step path.
   * @param {string} failedStepPath - Failed step path.
   * @param {string} paymentMethod - Payment method of the choice, e.g., "primer or "paypal" etc.
   * @param {boolean} restrictCardSave - Flag for card save - true || false
   * @returns {object} Checkout object with URL, invoice and token, etc.
   */
  const createCheckoutSession = async (
    successStepPath,
    failedStepPath,
    paymentMethod,
    restrictCardSave,
  ) => {
    try {
      const store = Alpine.store("data")

      if (!store.values) {
        throw new Error("Store values not found")
      }
      if (!store.values.activeOffer) {
        throw new Error("Active offer not found")
      }
      if (!store.values.userId) {
        throw new Error("User ID not found")
      }

      const paymentMethodId = getPaymentMethodId(paymentMethod)
      const customParameters = getCustomParameters(
        successStepPath,
        failedStepPath,
        restrictCardSave,
      )
      const offerId = store.values.activeOffer.brandOfferId
      const userId = store.values.userId

      const request = await fetch(
        `${API_URL}pay/checkout/${offerId}/${userId}/${paymentMethodId}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify(customParameters),
        },
      )
      const response = await request.json()

      if (!response.url) {
        throw new Error(`Payment processing failed: ${response.error}`)
      }

      return response
    } catch (e) {
      throw e
    }
  }

  /**
   * Creates a checkout session and redirects the user to the payment URL.
   * @param {Object} parameters - The parameters object.
   * @param {string} parameters.successStepPath - Success step path.
   * @param {string} parameters.failedStepPath - Failed step path.
   * @param {string} parameters.paymentMethod - Payment method of the choice, e.g., "primer or "paypal" etc.
   * @param {boolean} parameters.restrictCardSave - Flag for card save - true || false
   * @param {string} parameters.checkoutTemplate - Checkout template, possible to be undefined, then default will be shown
   */
  const handleCheckoutAndRedirect = async (parameters) => {
    try {
      const requiredParams = [
        "successStepPath",
        "failedStepPath",
        "paymentMethod",
        "restrictCardSave",
      ]
      checkParams(requiredParams, parameters)

      const { successStepPath, failedStepPath, paymentMethod, restrictCardSave } = parameters
      const response = await createCheckoutSession(
        successStepPath,
        failedStepPath,
        paymentMethod,
        restrictCardSave,
      )

      // For primer display the new checkout template
      if (
        parameters.checkoutTemplate &&
        parameters.checkoutTemplate !== "" &&
        paymentMethod === "primer"
      ) {
        window.location.assign(`${response.url}/${parameters.checkoutTemplate}`)
        return
      }

      // Move user to api-diet payment page
      window.location.assign(response.url)
    } catch (e) {
      console.error(`Error during checkout and redirect:`, e)
    }
  }
  // End: Main function

  return { handleCheckoutAndRedirect }
}

export default initApiDietPaymentProcessor
